"use client";
import React, { useState, useEffect } from 'react';
import styles from './BlogLeaveReplyComponent.module.css';
import { useTranslation } from 'react-i18next';
import { submitLeaveAReplyForm } from '../../src/services/api';  

const BlogLeaveReplyComponent = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    comment: '',
    name: '',
    email: '',
    website: '',
    saveInfo: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: false, message: '' });
  const [isFormInvalid, setIsFormInvalid] = useState(true);

  const validateField = (name, value, t) => {
    let errorMessage = '';

    switch (name) {
      case 'comment':
        if (value.trim() === '') {
          errorMessage = 'Please enter a valid comment.';
        }
        break;

      case 'name':
        if (!/^[a-zA-Z\s]{3,}$/.test(value)) {
          errorMessage = 'Please enter a valid name (3+ letters).';
        }
        break;

      case 'email':
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errorMessage = 'Please enter a valid email.';
        }
        break;

      case 'website':
        if (value && !/^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/.test(value)) {
          errorMessage = 'Please enter a valid URL (or leave empty).';
        }
        break;

      default:
        break;
    }

    return errorMessage;
  };


  // Validate form on every change to update button state
  useEffect(() => {
    const newErrors = {};
    let hasErrors = false;
    ['comment', 'name', 'email', 'website'].forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
        hasErrors = true;
      }
    });
    setIsFormInvalid(hasErrors);
  }, [formData, t]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
  };

  const dismissError = (field) => {
    setErrors((prevErrors) => ({ ...prevErrors, [field]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate all fields
    const newErrors = {};
    ['comment', 'name', 'email', 'website'].forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsFormInvalid(true);
      return;
    }
    setIsSubmitting(true);
    try {
      // Call the API with the form data
      await submitLeaveAReplyForm(formData);
      setSubmitStatus({ success: true, message: t('Comment submitted successfully!') });
      // Reset form after successful submission
      setFormData({
        comment: '',
        name: '',
        email: '',
        website: '',
        saveInfo: formData.saveInfo // Maintain the saveInfo preference
      });
      setErrors({});
      setIsFormInvalid(true); // Reset to invalid after submission
    } catch (error) {
      console.error('API Error:', error);
      setSubmitStatus({
        success: false,
        message: t('Failed to submit comment. Please try again.')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.replyContainer}>
      <h2 className={styles.replyTitle}>{t('Leave a Reply')}</h2>
      <form onSubmit={handleSubmit} className={styles.replyForm}>
        <div className={styles.formGroup}>
          <label htmlFor="comment" className={styles.formLabel}>
            {t('Comment')} <span className={styles.required}>*</span>
          </label>
          {errors.comment && (
            <div className={styles.errorOverlay}>
              {errors.comment}
              <button
                type="button"
                className={styles.dismissButton}
                onClick={() => dismissError('comment')}
                aria-label={t('Dismiss comment error')}
              >
                ✕
              </button>
            </div>
          )}
          {!errors.comment && (
            <textarea
              id="comment"
              name="comment"
              value={formData.comment}
              onChange={handleChange}
              onBlur={handleBlur}
              className={styles.textareaField}
              required
              aria-invalid={!!errors.comment}
            />
          )}
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="name" className={styles.formLabel}>
              {t('Name')} <span className={styles.required}>*</span>
            </label>
            {errors.name && (
              <div className={styles.errorOverlay}>
                {errors.name}
                <button
                  type="button"
                  className={styles.dismissButton}
                  onClick={() => dismissError('name')}
                  aria-label={t('Dismiss name error')}
                >
                  ✕
                </button>
              </div>
            )}
            {!errors.name && (
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                className={styles.inputField}
                required
                aria-invalid={!!errors.name}
              />
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.formLabel}>
              {t('Email')} <span className={styles.required}>*</span>
            </label>
            {errors.email && (
              <div className={styles.errorOverlay}>
                {errors.email}
                <button
                  type="button"
                  className={styles.dismissButton}
                  onClick={() => dismissError('email')}
                  aria-label={t('Dismiss email error')}
                >
                  ✕
                </button>
              </div>
            )}
            {!errors.email && (
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={styles.inputField}
                required
                aria-invalid={!!errors.email}
              />
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="website" className={styles.formLabel}>
              {t('Website')}
            </label>
            {errors.website && (
              <div className={styles.errorOverlay}>
                {errors.website}
                <button
                  type="button"
                  className={styles.dismissButton}
                  onClick={() => dismissError('website')}
                  aria-label={t('Dismiss website error')}
                >
                  ✕
                </button>
              </div>
            )}
            {!errors.website && (
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                onBlur={handleBlur}
                className={styles.inputField}
                aria-invalid={!!errors.website}
              />
            )}
          </div>
        </div>

        <div className={styles.checkboxGroup}>
          <input
            type="checkbox"
            id="saveInfo"
            name="saveInfo"
            checked={formData.saveInfo}
            onChange={handleChange}
            className={styles.checkbox}
          />
          <label htmlFor="saveInfo" className={styles.checkboxLabel}>
            {t('Save my name, email, and website in this browser for the next time I comment.')}
          </label>
        </div>

        <button
          type="submit"
          className={styles.submitButton}
          disabled={isSubmitting || isFormInvalid}
          aria-disabled={isSubmitting || isFormInvalid}
        >
          {isSubmitting ? t('Submitting...') : t('Post Comment')}
        </button>

        {submitStatus.message && (
          <div className={`${styles.submitStatus} ${submitStatus.success ? styles.success : styles.error}`}>
            {submitStatus.message}
          </div>
        )}
      </form>
    </div>
  );
};

export default BlogLeaveReplyComponent;